import styles from "./StockInsiders.module.scss";
import React, { FC, useEffect, useState, type JSX } from "react";
import { Stock } from "@/models/stock";
import Module from "@/components/module/Module";
import { getStockInsiderPeople, getStockInsidersBasics, getStockInsidersFlags, getStockInsidersPositions, getStockInsidersTransactions } from "@/controllers/stock-insiders";
import { useAtomValue } from "jotai";
import { lighterBackgroundColorState, textColorState } from "@/lib/store";
import dayjs from "dayjs";
import { useToggle } from "react-use";

import StockInsiderMomentum from "../stock-insider-momentum/StockInsiderMomentum";
import InsiderTradesAndPerformance from "../stock-insider-trades-and-performance/InsiderTradesAndPerformance";
import OutlinedBtn from "../outlined-btn/OutlinedBtn";
import { DataGrid } from "../data-grid/DataGrid";
import { Box } from "@mui/material";

const StockInsiders: FC<{ stock: Stock }> = ({ stock }) => {
    const textColor = useAtomValue(textColorState);
    const lighterBackgroundColor = useAtomValue(lighterBackgroundColorState);

    const [flags, toggleFlags] = useToggle(true);

    const [alignment, setAlignment] = React.useState<string | null>("left");

    const handleAlignment = (event: React.MouseEvent<HTMLElement>, newAlignment: string | null) => {
        setAlignment(newAlignment);
    };

    const [obfuscated, setObfuscated] = useState(false);
    const [chartObfuscated, setChartObfuscated] = useState(false);
    const [insidersPositionFilter, setInsidersPositionFilter] = useState<JSX.Element>();
    const [insidersTransactionFilter, setInsidersTransactionFilter] = useState<JSX.Element>();
    const [insiderPeopleFilter, setInsiderPeopleFilter] = useState<JSX.Element>();

    // #2098: the insider grids are addressed by trading item, not by the
    // screening stock id. The two are different numbers, and passing the
    // wrong one returns another company's filings rather than an error.
    const tradingitemId = stock.tradingitem_id;

    useEffect(() => {
        getStockInsidersFlags(stock.stock_id, "2000-01-01", dayjs().format("YYYY-MM-DD")).then((res) => {
            setChartObfuscated(res.obfuscated);
        });
        getStockInsidersBasics(stock.stock_id).then((data) => {
            setObfuscated(data.obfuscated);
        });
    }, []);

    const renderFilters = (
        <>
            <style>
                {`
                .MuiInputBase-root .MuiSvgIcon-root {
                    fill: ${textColor} !important;
                    color: ${textColor} !important;
                }
            `}
            </style>
            <div className={styles.filtersContainer}>
                <OutlinedBtn onClick={toggleFlags}>{`${flags ? "Hide" : "Show"} flags`}</OutlinedBtn>
            </div>
        </>
    );

    return (
        <Box className={styles.StockInsiders}>
            <style jsx>{`
                .${styles.button} {
                    color: ${textColor};
                }
                .${styles.selector} {
                    cursor: ${obfuscated ? "unset" : "pointer"};
                }
                @media (hover: hover) {
                    .${styles.button}:hover {
                        background-color: ${lighterBackgroundColor} !important;
                    }
                }
            `}</style>
            <div className={styles.momentum}>
                <Module title={`${stock.exchangesymbol}:${stock.ticker} Insider Momentum`} help={{ moduleId: "insider_momentum", modulePage: "V2_stock_insiders" }}>
                    <StockInsiderMomentum stock={stock} obfuscated={obfuscated} />
                </Module>
            </div>
            <div className={styles.positions}>
                <Module title={`Current Insider Positions in ${stock.exchangesymbol}:${stock.ticker}`} help={{ moduleId: "insider_positions", modulePage: "V2_stock_insiders" }} allowFullscreen filters={insidersPositionFilter}>
                    {tradingitemId && <DataGrid fetcher={getStockInsidersPositions} id={tradingitemId} setFilters={setInsidersPositionFilter} />}
                </Module>
            </div>
            <div className={styles.transactions}>
                <Module title={`Recent Reported Insider Transactions in ${stock.exchangesymbol}:${stock.ticker}`} help={{ moduleId: "insider_transactions", modulePage: "V2_stock_insiders" }} allowFullscreen filters={insidersTransactionFilter}>
                    {tradingitemId && <DataGrid fetcher={getStockInsidersTransactions} id={tradingitemId} setFilters={setInsidersTransactionFilter} />}
                </Module>
            </div>
            <div className={styles.people}>
                <Module title={`Insiders at ${stock.exchangesymbol}:${stock.ticker}`} help={{ moduleId: "insider_people", modulePage: "V2_stock_insiders" }} allowFullscreen filters={insiderPeopleFilter}>
                    {tradingitemId && <DataGrid fetcher={getStockInsiderPeople} id={tradingitemId} setFilters={setInsiderPeopleFilter} />}
                </Module>
            </div>
            <div className={styles.chart}>
                <Module
                    title={`${stock.exchangesymbol}:${stock.ticker} Insider Trades and Performance`}
                    help={{ moduleId: "insider_chart", modulePage: "V2_stock_insiders" }}
                    filters={renderFilters}
                    noPadding={{ source: "body", type: "mobile" }}
                    feedback
                >
                    <InsiderTradesAndPerformance stock={stock} flags={flags} chartObfuscated={chartObfuscated} />
                </Module>
            </div>
        </Box>
    );
};

export default StockInsiders;
